import { useState, useEffect, useCallback } from 'react';
import { useWorkspaceStore } from '@/store/workspace';
import {
  getWorkspaceMembers,
  inviteWorkspaceMember,
  removeWorkspaceMember,
  revokeInvitation,
  updateMemberRole,
} from '@/lib/database';
import { MemberWithEmail, WorkspaceInvitation } from '@/types';
import { track } from '@/lib/posthog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Users,
  UserPlus,
  Trash2,
  Loader2,
  Mail,
  Clock,
  CheckCircle,
  AlertTriangle,
  Shield,
  Eye,
} from 'lucide-react';

export function MembersPage() {
  const { currentWorkspace } = useWorkspaceStore();
  const canEdit = useWorkspaceStore((s) => s.canEdit());

  const [members, setMembers] = useState<MemberWithEmail[]>([]);
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'reader'>('reader');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Remove/revoke state
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null);
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!currentWorkspace) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getWorkspaceMembers(currentWorkspace.id);
      setMembers(data.members);
      setInvitations(data.invitations);
    } catch (err: any) {
      setError(err.message || 'Failed to load members');
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentWorkspace || !inviteEmail.trim()) return;

    setIsInviting(true);
    setInviteError(null);
    setInviteSuccess(null);

    try {
      const result = await inviteWorkspaceMember(
        currentWorkspace.id,
        inviteEmail.trim(),
        inviteRole
      );
      setInviteSuccess(
        result.status === 'added'
          ? `${inviteEmail} has been added to the workspace`
          : `Invitation sent to ${inviteEmail} (they'll join when they sign up)`
      );
      track('member_invited', {
        workspace_id: currentWorkspace.id,
        role: inviteRole,
        invite_status: result.status,
      });
      setInviteEmail('');
      setInviteRole('reader');
      await fetchMembers();
      setTimeout(() => setInviteSuccess(null), 5000);
    } catch (err: any) {
      setInviteError(err.message || 'Failed to invite member');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemove = async (userId: string, email: string) => {
    if (!currentWorkspace) return;
    if (!confirm(`Remove ${email} from this workspace?`)) return;

    setRemovingUserId(userId);
    try {
      await removeWorkspaceMember(currentWorkspace.id, userId);
      track('member_removed', { workspace_id: currentWorkspace.id });
      await fetchMembers();
    } catch (err: any) {
      alert(err.message || 'Failed to remove member');
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'admin' | 'reader') => {
    if (!currentWorkspace) return;
    setUpdatingRoleUserId(userId);
    try {
      await updateMemberRole(currentWorkspace.id, userId, newRole);
      track('member_role_updated', { workspace_id: currentWorkspace.id, role: newRole });
      await fetchMembers();
    } catch (err: any) {
      // Backend guards against e.g. demoting the last admin — surface that.
      alert(err.message || 'Failed to update role');
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  const handleRevoke = async (invitationId: string, email: string) => {
    if (!currentWorkspace) return;
    if (!confirm(`Revoke invitation for ${email}?`)) return;

    setRevokingInviteId(invitationId);
    try {
      await revokeInvitation(currentWorkspace.id, invitationId);
      await fetchMembers();
    } catch (err: any) {
      alert(err.message || 'Failed to revoke invitation');
    } finally {
      setRevokingInviteId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <Users className="h-6 w-6" />
        Members
      </h1>

      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Invite Form — admin only */}
      {canEdit && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Invite Member
            </CardTitle>
            <CardDescription>
              Add someone by email. If they don't have an account yet, they'll join when they sign up.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  disabled={isInviting}
                  className="flex-1"
                  data-testid="invite-email-input"
                />
                <Select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'reader')}
                  disabled={isInviting}
                  data-testid="invite-role-select"
                >
                  <option value="reader">Reader</option>
                  <option value="admin">Admin</option>
                </Select>
                <Button type="submit" disabled={isInviting || !inviteEmail.trim()} data-testid="invite-submit-btn">
                  {isInviting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Invite
                    </>
                  )}
                </Button>
              </div>
              {inviteError && (
                <p className="text-sm text-red-600 flex items-center gap-1" data-testid="invite-error">
                  <AlertTriangle className="h-4 w-4" />
                  {inviteError}
                </p>
              )}
              {inviteSuccess && (
                <p className="text-sm text-green-600 flex items-center gap-1" data-testid="invite-success">
                  <CheckCircle className="h-4 w-4" />
                  {inviteSuccess}
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      {/* Current Members */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg" data-testid="members-count">
            Current Members ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {members.length === 0 ? (
            <p className="text-sm text-gray-500">No members</p>
          ) : (
            <div className="space-y-3">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50"
                  data-testid="member-item"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                      {(member.name || member.email)?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {member.name || member.email}
                      </p>
                      {member.name && (
                        <p className="text-xs text-gray-500">{member.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canEdit ? (
                      <div className="flex items-center gap-1.5">
                        {updatingRoleUserId === member.user_id && (
                          <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                        )}
                        <Select
                          value={member.role}
                          onChange={(e) =>
                            handleRoleChange(member.user_id!, e.target.value as 'admin' | 'reader')
                          }
                          disabled={updatingRoleUserId === member.user_id}
                          className="h-8 text-xs"
                          data-testid="member-role-select"
                          aria-label={`Role for ${member.name || member.email}`}
                        >
                          <option value="reader">Reader</option>
                          <option value="admin">Admin</option>
                        </Select>
                      </div>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          member.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                        data-testid="member-role"
                      >
                        {member.role === 'admin' ? (
                          <Shield className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                        {member.role}
                      </span>
                    )}
                    {canEdit && members.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(member.user_id!, member.email)}
                        disabled={removingUserId === member.user_id}
                        className="text-gray-400 hover:text-red-600"
                        data-testid="remove-member-btn"
                      >
                        {removingUserId === member.user_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card data-testid="pending-invitations-section">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />
              Pending Invitations ({invitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50"
                  data-testid="pending-invite-item"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center text-sm font-medium text-amber-600">
                      <Mail className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                      <p className="text-xs text-gray-500">
                        Invited {new Date(inv.invited_at).toLocaleDateString()} &middot; Expires{' '}
                        {new Date(inv.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        inv.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {inv.role}
                    </span>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRevoke(inv.id, inv.email)}
                        disabled={revokingInviteId === inv.id}
                        className="text-gray-400 hover:text-red-600"
                        data-testid="revoke-invite-btn"
                      >
                        {revokingInviteId === inv.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
